"use client";

import { FC, useState } from "react";
import clsx from "clsx";
import { useI18n } from "@/i18n/client";
import { StoreRegion } from "@/lib/_config/stores";
import NewsCard from "../NewsCard/NewsCard";
import Pagination from "@/components/UI/Pagination/Pagination";
import styles from "./LatestNews.module.scss";
import { ArticleEntryType } from "@/lib/builder/server-queries";
import { getStorePrefix, translatePath } from "@/i18n/paths/path-translator";

interface Props {
	articles: any[];
	tags: string[] | undefined;
	store: StoreRegion;
	lang: string;
	locale?: string;
	title: ArticleEntryType;
}

const PAGE_SIZE = 9;

const stripBuilderLocalization = (value: any, locale?: string): string => {
	if (typeof value === "string") return value;
	if (value && typeof value === "object") {
		if (locale && value[locale]) return value[locale];
		if (value.Default) return value.Default;
		// Fallback to first available value that is a string
		for (const key in value) {
			if (typeof value[key] === "string" && key !== "@type") return value[key];
		}
	}
	return "";
};

const LatestNews: FC<Props> = ({
	articles,
	tags,
	store,
	lang,
	locale,
	title,
}) => {
	const pageCount = Math.ceil(articles.length / PAGE_SIZE);

	const [activePage, setActivePage] = useState(0);

	const t = useI18n();
	const resolvedLocale =
		locale ||
		(store !== "global" && lang ? `${lang}-${store.toUpperCase()}` : "") ||
		"en";
	const prefix =
		store !== "global" ? getStorePrefix(store, resolvedLocale) : "";

	const getPage = (page: number) => {
		const foundPage = articles.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
		return foundPage;
	};

	return (
		<div
			className={clsx([styles.container, tags !== undefined && styles.hasTags])}
		>
			{!tags && (
				<h2 className={styles.title}>
					{title === "news" ? t("Blog.latestNews") : t("Blog.latestBlogs")}
				</h2>
			)}
			{tags && (
				<h2 className={styles.title}>
					{t("Blog.tag")}: {tags.join(", ")}
				</h2>
			)}
			<div className={styles.grid}>
				{getPage(activePage)?.map((article) => (
					<NewsCard
						key={article.id}
						title={stripBuilderLocalization(article.data.title, lang)}
						link={translatePath({
							path: stripBuilderLocalization(article.data.url, lang).replace(
								/^\//,
								"",
							),
							locale: resolvedLocale,
							prefix,
						})}
						date={article.data.date}
						image={stripBuilderLocalization(article.data.featuredImage, lang)}
						precis={stripBuilderLocalization(article.data.description, lang)}
					/>
				))}
			</div>
			<Pagination
				pageCount={pageCount}
				page={activePage}
				onPageChange={setActivePage}
			/>
		</div>
	);
};

export default LatestNews;
